import { ComponentIds, HierarchyUtils } from '@inter-ikea-core/core';
import { PlannerEcsMultiSystem } from './System';
import { ProductType } from '../../types';
// EketConnectionFittingsSystem
// This system handles the amount of connection fittings that should be added to the scene
// depending on how many stacked frames there are on floor standing frames
// Every stacked frame should add 2 fittings
export class EketConnectionFittingsSystem extends PlannerEcsMultiSystem {
    constructor(coreApi) {
        super(coreApi, 'EketConnectionFittingsSystem', [ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID]);
        this.connectionFitting = '80334617';
        this.guestStackingConnectorTag = 'stacking-guest';
        this.fittingsPerFrame = 2;
    }
    onAdd(entity) {
        if (entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
            this.updateConnectionFittingsInScene();
        }
    }
    onUpdate(entity, oldComponent, newComponent) {
        if (oldComponent.eq(newComponent))
            return;
        if (entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
            this.updateConnectionFittingsInScene();
        }
    }
    onRemove(entity) {
        if (entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
            this.updateConnectionFittingsInScene();
        }
    }
    removeCurrentConnectionFittingsInScene() {
        const connectionFittingsInScene = this.coreApi.entities.getByProductType(ProductType.EKET_CONNECTION_FITTING);
        connectionFittingsInScene.forEach(connectionFitting => this.coreApi.getServices().entityManager.disposeEntity(connectionFitting));
    }
    addConnectionFittingsToScene(fittingsToAdd) {
        // Shorthand to loop through an empty array initialised with the length of the amount of fittings to add to the scene
        Array.from(Array(fittingsToAdd).keys()).forEach(() => {
            const connectionFittingEntity = this.coreApi
                .getServices()
                .productManager.createEntity(this.connectionFitting);
            if (connectionFittingEntity) {
                this.coreApi.entities.placementService.forcePlaceEntity(connectionFittingEntity);
            }
        });
    }
    // Searches an frames hierarchy and sees if it can find a descendant that has the frame component with the stacking connector
    // In that case we want to add connection fittings
    getFrameDescendentWithFrameComponentAndStackingConnector(frame) {
        return HierarchyUtils.descendants(frame).filter(descendent => {
            return (descendent.hasComponent(ComponentIds.FRAME_COMPONENT_ID) &&
                // If we dont filter by this key the frame with glass door assembly will not work correctly as it has a frame in the hierarchy
                descendent
                    .getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)
                    ?.highestPriorityConnection?.guestConnector.tags()
                    .find(t => t === this.guestStackingConnectorTag));
        });
    }
    // Get all floor standing frames and calculate how many stacked frames there are
    // on those so we can correctly calculate the amount of connection fittings
    // That we should add to the scene
    updateConnectionFittingsInScene() {
        const framesInScene = this.coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID, 'eket');
        const floorStandingFrames = framesInScene.filter(frame => this.coreApi.entities.isEntityPlacedOnFloor(frame));
        const floorStandingStackedFrames = floorStandingFrames.flatMap(frame => {
            const entitygroup = HierarchyUtils.findWithComponentInUpwardsHierarchy(frame, ComponentIds.AUTOMATIC_ENTITY_GROUP_COMPONENT_ID);
            // If the entity is part of an entity group (eket 2 single frames with a stacked wider frame on top for example)
            // We need to search through the entity group instead of the single frame otherwise we can't find the stacked frames
            if (entitygroup) {
                return this.getFrameDescendentWithFrameComponentAndStackingConnector(entitygroup);
            }
            else {
                return this.getFrameDescendentWithFrameComponentAndStackingConnector(frame);
            }
        });
        const amountOfConnectionFittingsToAdd = floorStandingStackedFrames.length * this.fittingsPerFrame;
        // Remove all previous fittings as it's easier than keeping track of all the fittings in the scene when loading vpc etc.
        this.removeCurrentConnectionFittingsInScene();
        // Add all the new fittings to the scene
        this.addConnectionFittingsToScene(amountOfConnectionFittingsToAdd);
    }
}
//# sourceMappingURL=EketConnectionFittingsSystem.js.map